import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DiscountCouponService from "./Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import PropTypes from 'prop-types';
import { Col, CardBody, Card } from "reactstrap";
import PermissionAccess from '../../PermissionAccess/Access.js';

const checklistItems = [
  { key: "ic_verification_approved", label: "IC Verification Approved" },
  { key: "ic_numbers_match", label: "Manual IC & OCR IC Match" },
  { key: "profile_image_uploaded", label: "Profile Image Uploaded" },
  { key: "face_left_uploaded", label: "Left Face Image Uploaded" },
  { key: "face_right_uploaded", label: "Right Face Image Uploaded" },
];

const EditPatientModal = ({ showModalEditPatient, handleToggleModalEditPatient, id, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { authData } = PermissionAccess();

  const [data, setData] = useState({
    status: '',
    rejection_message: '',
  });
  const [approvalChecklist, setApprovalChecklist] = useState(null);

  const [errors, setErrors] = useState({
    status: '',
    rejection_message: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await DiscountCouponService.GetSinglePatient(authData.sessionID, id);
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else if (response.data.status) {
          const patientData = response.data.data?.data || response.data.data || {};
          const checklist = response.data.data?.approval_checklist || null;
          setApprovalChecklist(checklist);
          // Normalize legacy Active/InActive to Approved/Rejected for the status dropdown
          let accountStatus = patientData.status || '';
          if (accountStatus === 'Active') accountStatus = 'Approved';
          if (accountStatus === 'InActive' || accountStatus === 'Inactive') accountStatus = 'Rejected';
          setData({
            status: accountStatus,
            rejection_message: '',
          });
        } else {
          Alerts.swalErrorAlert(t("Something went wrong, Please try again."));
        }
      } catch (error) {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
      }
    };
    if (id) fetchData();
  }, [id, authData.sessionID, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
      ...(name === 'status' && value !== 'Rejected' ? { rejection_message: '' } : {}),
    }));

    if (name === 'status') {
      setErrors((prev) => ({ ...prev, status: '' }));
    }

    if (name === 'rejection_message') {
      setErrors((prev) => ({ ...prev, rejection_message: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let validationErrors = {};

    if (!data.status) validationErrors.status = 'Status is required';
    if (data.status === 'Rejected' && !String(data.rejection_message || '').trim()) {
      validationErrors.rejection_message = 'Rejection reason is required';
    }
    if (
      data.status === 'Approved' &&
      approvalChecklist &&
      approvalChecklist.can_approve === false
    ) {
      validationErrors.status =
        (approvalChecklist.blockers && approvalChecklist.blockers.join(' ')) ||
        'Patient does not meet approval requirements.';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      try {
        const response = await DiscountCouponService.EditPatient(authData.sessionID, data, id);
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else if (response.data.status) {
          handleToggleModalEditPatient();
          onSuccess();
          Alerts.swalSuccessAlert(t("Status updated successfully."));
        } else {
          if (response.data.data?.approval_checklist) {
            setApprovalChecklist(response.data.data.approval_checklist);
          }
          validationErrors.status = response.data.message || 'An error occurred';
          setErrors(validationErrors);
        }
      } catch (error) {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
      }
    }
  };

  const handleClose = () => {
    setErrors({
      status: '',
      rejection_message: '',
    });
    handleToggleModalEditPatient();
  };

  return (
    <div
      className={showModalEditPatient ? "modal d-block mt-4" : "modal"}
      style={{
        display: showModalEditPatient ? 'block' : 'none',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      tabIndex="-1"
      role="dialog"
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editPatientAccountStatusLabel">{t("Status")}</h5>
            </div>
            <div className="modal-body">
              <Col md="12">
                <Card className="main-card mb-3">
                  <CardBody>
                    <LoadingOverlay
                      tag="div"
                      active={loading}
                      styles={{ overlay: (base) => ({ ...base }) }}
                      spinner={<DNALoader />}
                    >
                      {approvalChecklist && (
                        <div className="mb-3 p-3" style={{ background: "#f7f9fc", borderRadius: "6px" }}>
                          <strong>{t("Approval Requirements")}</strong>
                          <ul className="mb-0 mt-2" style={{ paddingLeft: "18px" }}>
                            {checklistItems.map((item) => {
                              const ok = approvalChecklist.checks?.[item.key];
                              return (
                                <li key={item.key} style={{ color: ok ? "#1BD35A" : "#FF0000" }}>
                                  {ok ? "✓" : "✗"} {t(item.label)}
                                </li>
                              );
                            })}
                          </ul>
                          {!approvalChecklist.can_approve && (
                            <small className="text-danger d-block mt-2">
                              {t("Approve is allowed only when all requirements are complete.")}
                            </small>
                          )}
                        </div>
                      )}

                      <div className="form-group">
                        <label htmlFor="patient-status" className="col-form-label">{t("Status")}:</label>
                        <select
                          className="form-control"
                          name="status"
                          onChange={handleChange}
                          value={data.status}
                        >
                          <option value="" disabled>{t("Select Status")}</option>
                          <option value="Pending">{t("Pending")}</option>
                          <option
                            value="Approved"
                            disabled={approvalChecklist && approvalChecklist.can_approve === false}
                          >
                            {t("Approved")}
                          </option>
                          <option value="Rejected">{t("Rejected")}</option>
                        </select>
                        {errors.status && <strong className="error text-danger">{errors.status}</strong>}
                      </div>

                      {data.status === 'Rejected' && (
                        <div className="form-group mt-3">
                          <label htmlFor="rejection-message" className="col-form-label">{t("Rejection Reason")}:</label>
                          <textarea
                            className="form-control"
                            name="rejection_message"
                            rows="3"
                            onChange={handleChange}
                            value={data.rejection_message}
                            required
                          ></textarea>
                          {errors.rejection_message && <strong className="error text-danger">{errors.rejection_message}</strong>}
                        </div>
                      )}

                      <div className="text-center mt-4">
                        <button type="button" className="btn btn-outline-light btn-hover-shine me-3" onClick={handleClose}>{t("Close")}</button>
                        <button type="submit" className="btn btn-custom-color">{t("Save changes")}</button>
                      </div>

                    </LoadingOverlay>
                  </CardBody>
                </Card>
              </Col>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

EditPatientModal.propTypes = {
  showModalEditPatient: PropTypes.bool.isRequired,
  handleToggleModalEditPatient: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default EditPatientModal;
